import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const FEATURES = ['posts', 'approvals', 'brand_guide', 'calendar', 'client_settings'];
const PERMISSION_LEVELS = ['view', 'edit', 'approve'];

const FEATURE_LABELS = {
  posts: 'Posts',
  approvals: 'Aprovacoes',
  brand_guide: 'Brand Guide',
  calendar: 'Calendario',
  client_settings: 'Config. Cliente',
};

function UserPermissions() {
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userClients, setUserClients] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [{ data: profilesData }, { data: clientsData }] = await Promise.all([
      supabase.from('agency_profiles').select('*').in('role', ['cliente', 'colaborador']).eq('active', true).order('full_name'),
      supabase.from('agency_clients').select('id, name').order('name'),
    ]);
    setUsers(profilesData || []);
    setClients(clientsData || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loadUserAccess = useCallback(async (user) => {
    setSelectedUser(user);

    if (user.role === 'cliente') {
      const { data } = await supabase
        .from('agency_cliente_users')
        .select('client_id')
        .eq('user_id', user.id);
      setUserClients((data || []).map(r => r.client_id));
      setUserPermissions([]);
    } else if (user.role === 'colaborador') {
      const [{ data: clientsData }, { data: permsData }] = await Promise.all([
        supabase.from('agency_colaborador_clients').select('client_id').eq('colaborador_id', user.id),
        supabase.from('agency_colaborador_permissions').select('*').eq('colaborador_id', user.id),
      ]);
      setUserClients((clientsData || []).map(r => r.client_id));
      setUserPermissions(permsData || []);
    }
  }, []);

  const toggleClientAccess = async (clientId) => {
    if (!selectedUser) return;
    setSaving(true);

    const hasAccess = userClients.includes(clientId);
    const table = selectedUser.role === 'cliente' ? 'agency_cliente_users' : 'agency_colaborador_clients';
    const idField = selectedUser.role === 'cliente' ? 'user_id' : 'colaborador_id';

    try {
      if (hasAccess) {
        // Remove acesso
        await supabase.from(table).delete().eq(idField, selectedUser.id).eq('client_id', clientId);

        // Remove permissoes do colaborador para este cliente
        if (selectedUser.role === 'colaborador') {
          await supabase
            .from('agency_colaborador_permissions')
            .delete()
            .eq('colaborador_id', selectedUser.id)
            .eq('client_id', clientId);
        }

        setUserClients(prev => prev.filter(c => c !== clientId));
        setUserPermissions(prev => prev.filter(p => p.client_id !== clientId));
      } else {
        // Adiciona acesso
        await supabase.from(table).insert({ [idField]: selectedUser.id, client_id: clientId });
        setUserClients(prev => [...prev, clientId]);
      }
    } catch (err) {
      showToast('error', `Erro: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const setPermission = async (clientId, feature, level) => {
    if (!selectedUser || selectedUser.role !== 'colaborador') return;
    setSaving(true);

    try {
      const existing = userPermissions.find(
        p => p.client_id === clientId && p.feature === feature
      );

      if (level === 'none') {
        // Remove permissao
        if (existing) {
          await supabase.from('agency_colaborador_permissions').delete().eq('id', existing.id);
          setUserPermissions(prev => prev.filter(p => p.id !== existing.id));
        }
      } else if (existing) {
        // Atualiza permissao
        await supabase
          .from('agency_colaborador_permissions')
          .update({ permission: level })
          .eq('id', existing.id);
        setUserPermissions(prev =>
          prev.map(p => (p.id === existing.id ? { ...p, permission: level } : p))
        );
      } else {
        // Cria permissao
        const { data } = await supabase
          .from('agency_colaborador_permissions')
          .insert({
            colaborador_id: selectedUser.id,
            client_id: clientId,
            feature,
            permission: level,
          })
          .select()
          .single();

        if (data) {
          setUserPermissions(prev => [...prev, data]);
        }
      }
    } catch (err) {
      showToast('error', `Erro: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const getPermissionLevel = (clientId, feature) => {
    const perm = userPermissions.find(
      p => p.client_id === clientId && p.feature === feature
    );
    return perm ? perm.permission : 'none';
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner" />
        Carregando...
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="admin-page-header">
          <div>
            <h1 className="page-title">Permissoes de Usuarios</h1>
            <p className="page-subtitle">Atribuir clientes e permissoes granulares</p>
          </div>
          <Link to="/admin/users" className="btn btn-secondary">Voltar</Link>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 'var(--spacing-lg)' }}>
        {/* Lista de usuarios */}
        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: 'var(--spacing-md)', borderBottom: '1px solid var(--color-border)' }}>
            <h3 style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>Usuarios</h3>
          </div>
          {users.map(user => (
            <div
              key={user.id}
              onClick={() => loadUserAccess(user)}
              style={{
                padding: 'var(--spacing-sm) var(--spacing-md)',
                cursor: 'pointer',
                borderBottom: '1px solid var(--color-border)',
                background: selectedUser?.id === user.id ? 'var(--color-bg-card)' : 'transparent',
              }}
            >
              <div style={{ fontWeight: selectedUser?.id === user.id ? 600 : 400 }}>
                {user.full_name || user.email}
              </div>
              <div style={{ display: 'flex', gap: 'var(--spacing-xs)', alignItems: 'center' }}>
                <span className={`role-badge role-badge--${user.role}`}>{user.role}</span>
                <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{user.email}</span>
              </div>
            </div>
          ))}
          {users.length === 0 && (
            <p style={{ padding: 'var(--spacing-md)', color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
              Nenhum usuario cliente/colaborador encontrado.
            </p>
          )}
        </div>

        {/* Painel de permissoes */}
        <div>
          {!selectedUser ? (
            <div className="card" style={{ textAlign: 'center', padding: 'var(--spacing-2xl)' }}>
              <p style={{ color: 'var(--color-text-muted)' }}>Selecione um usuario para gerenciar permissoes</p>
            </div>
          ) : (
            <div>
              <h2 style={{ fontSize: '1.1rem', marginBottom: 'var(--spacing-md)' }}>
                {selectedUser.full_name || selectedUser.email}
                <span className={`role-badge role-badge--${selectedUser.role}`} style={{ marginLeft: 'var(--spacing-sm)' }}>
                  {selectedUser.role}
                </span>
              </h2>

              {/* Clientes atribuidos */}
              <div className="card" style={{ marginBottom: 'var(--spacing-lg)' }}>
                <h3 style={{ fontSize: '0.95rem', marginBottom: 'var(--spacing-md)' }}>Clientes Atribuidos</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                  {clients.map(client => (
                    <label
                      key={client.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-sm)',
                        padding: 'var(--spacing-xs) var(--spacing-sm)',
                        cursor: 'pointer',
                        borderRadius: 'var(--radius-sm)',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={userClients.includes(client.id)}
                        onChange={() => toggleClientAccess(client.id)}
                        disabled={saving}
                      />
                      <span>{client.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Permissoes granulares (apenas colaborador) */}
              {selectedUser.role === 'colaborador' && userClients.length > 0 && (
                <div className="card">
                  <h3 style={{ fontSize: '0.95rem', marginBottom: 'var(--spacing-md)' }}>Permissoes Granulares</h3>
                  <div className="permissions-grid">
                    {userClients.map(clientId => {
                      const client = clients.find(c => c.id === clientId);
                      if (!client) return null;
                      return (
                        <div key={clientId} className="permission-client-card">
                          <div className="permission-client-name">{client.name}</div>
                          <div className="permission-features">
                            {FEATURES.map(feature => (
                              <div key={feature} className="permission-feature">
                                <span className="permission-feature-name">{FEATURE_LABELS[feature]}</span>
                                <select
                                  className="form-input"
                                  style={{ width: 'auto', padding: '2px 6px', fontSize: '0.8rem' }}
                                  value={getPermissionLevel(clientId, feature)}
                                  onChange={(e) => setPermission(clientId, feature, e.target.value)}
                                  disabled={saving}
                                >
                                  <option value="none">Nenhum</option>
                                  {PERMISSION_LEVELS.map(level => (
                                    <option key={level} value={level}>{level}</option>
                                  ))}
                                </select>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className="toast-container">
          <div className={`toast toast--${toast.type}`}>{toast.message}</div>
        </div>
      )}
    </div>
  );
}

export default UserPermissions;
